import { AttendedLobbies } from '@listings/enums/attended-lobbies.enum';
import { BuildingPeriods } from '@listings/enums/building-periods.enum';
import { BuildingTypes } from '@listings/enums/building-types.enum';
import { CommonOutdoorSpaces } from '@listings/enums/common-outdoor-spaces.enum';
import { Features } from '@listings/enums/features.enum';
import { MiscellaneousDetails } from '@listings/enums/miscellaneous-details.enum';
import { OwnershipTypes } from '@listings/enums/ownership-types.enum';
import { PrivateOutdoorSpaces } from '@listings/enums/private-outdoor-spaces.enum';

import { Baths } from '../../toolbar-modules/on-market-secondary-toolbar/components/on-market-filter/enums/baths.enum';
import { Beds } from '../../toolbar-modules/on-market-secondary-toolbar/components/on-market-filter/enums/beds.enum';
import { Rooms } from '../../toolbar-modules/on-market-secondary-toolbar/components/on-market-filter/enums/rooms.enum';
import { CheckboxFilterValue } from '@search/components/checkbox-filter/models/checkbox-filter-value';

const SALES_PRICE_MIN_MAX_DATA = {
    min: 100_000,
    max: 150_000_000,
    slider: { min: 0, max: 15_000_000 },
    step: 100_000
} as const;

const RENTALS_PRICE_MIN_MAX_DATA = {
    min: 500,
    max: 100_000,
    slider: { min: 500, max: 100_000 },
    step: 500
} as const;

const BATHS_VALUES: CheckboxFilterValue[] = [
    { value: Baths.One, valueLocalizationKey: '1', order: 1 },
    { value: Baths.OneAndHalf, valueLocalizationKey: '1.5', order: 2 },
    { value: Baths.Two, valueLocalizationKey: '2', order: 3 },
    { value: Baths.Three, valueLocalizationKey: '3', order: 4 },
    { value: Baths.Four, valueLocalizationKey: '4', order: 5 }
];

const ROOMS_VALUES: CheckboxFilterValue[] = [
    { value: Rooms.One, valueLocalizationKey: '1', order: 1 },
    { value: Rooms.OneAndHalf, valueLocalizationKey: '1.5', order: 2 },
    { value: Rooms.Two, valueLocalizationKey: '2', order: 3 },
    { value: Rooms.Three, valueLocalizationKey: '3', order: 4 },
    { value: Rooms.Four, valueLocalizationKey: '4', order: 5 },
    { value: Rooms.Five, valueLocalizationKey: '5', order: 6 }
];

const BUILDING_PERIOD_VALUES: CheckboxFilterValue[] = [
    { value: BuildingPeriods.PreWar, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.BUILDING_PERIOD.PRE_WAR', order: 1 },
    { value: BuildingPeriods.PostWar, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.BUILDING_PERIOD.POST_WAR', order: 2 }
];

const ATTENDED_LOBBIES_VALUES: CheckboxFilterValue[] = [
    { value: AttendedLobbies.Any, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.ANY', order: 1 },
    { value: AttendedLobbies.DoormanFullTime, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.ATTENDED_LOBBY.DOORMAN_FULL_TIME', order: 2 },
    { value: AttendedLobbies.DoormanPartTime, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.ATTENDED_LOBBY.DOORMAN_PART_TIME', order: 3 },
    { value: AttendedLobbies.Concierge, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.ATTENDED_LOBBY.CONCIERGE', order: 4 },
    { value: AttendedLobbies.ElevatorMan, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.ATTENDED_LOBBY.ELEVATOR_MAN', order: 5 },
];

const BUILDING_TYPES_VALUES: CheckboxFilterValue[] = [
    { value: BuildingTypes.HighRise, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.BUILDING_TYPE.HIGHRISE', order: 1 },
    { value: BuildingTypes.LowRise, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.BUILDING_TYPE.LOWRISE', order: 2 },
    { value: BuildingTypes.Loft, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.BUILDING_TYPE.LOFT', order: 3 },
    { value: BuildingTypes.Townhouse, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.BUILDING_TYPE.TOWNHOUSE', order: 4 },
    { value: BuildingTypes.Brownstone, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.BUILDING_TYPE.BROWNSTONE', order: 5 },
    { value: BuildingTypes.Walkup, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.BUILDING_TYPE.WALKUP', order: 6 },
    { value: BuildingTypes.Office, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.BUILDING_TYPE.OFFICE', order: 7 },
    { value: BuildingTypes.Retail, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.BUILDING_TYPE.RETAIL', order: 8 },
    { value: BuildingTypes.Hotel, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.BUILDING_TYPE.HOTEL', order: 9 },
];

const COMMON_OUTDOOR_SPACES_VALUES: CheckboxFilterValue[] = [
    { value: CommonOutdoorSpaces.Courtyard, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.COMMON_OUTDOOR_SPACE.COURTYARD', order: 1 },
    { value: CommonOutdoorSpaces.RoofDeck, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.COMMON_OUTDOOR_SPACE.ROOF_DECK', order: 2 }
];

const FEATURES_VALUES: CheckboxFilterValue[] = [
    { value: Features.Cooling, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.FEATURES.COOLING', order: 1 },
    { value: Features.HealthClubFitness, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.FEATURES.HEALTH_CLUB_FITNESS', order: 2 },
    { value: Features.LaundryFacilities, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.FEATURES.LAUNDRY_FACILITIES', order: 3 },
    { value: Features.Parking, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.FEATURES.PARKING', order: 4 },
    { value: Features.Pool, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.FEATURES.POOL', order: 5 },
    { value: Features.Security, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.FEATURES.SECURITY', order: 6 },
    { value: Features.Storage, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.FEATURES.STORAGE', order: 7 },
    { value: Features.Wheelchair, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.FEATURES.WHEELCHAIR', order: 8 },
    { value: Features.Elevator, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.FEATURES.ELEVATOR', order: 9 }
];

const SALES_MISCELLANEOUS_DETAILS_VALUES: CheckboxFilterValue[] = [
    { value: MiscellaneousDetails.WasherDryer, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.WASHER_DRYER', order: 1 },
    { value: MiscellaneousDetails.SponsorApartment, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.SPONSOR_APARTMENT', order: 2 },
    { value: MiscellaneousDetails.DiningRoom, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.DINING_ROOM', order: 3 },
    { value: MiscellaneousDetails.EatInKitchen, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.EAT_IN_KITCHEN', order: 4 },
    { value: MiscellaneousDetails.MaidsRoom, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.MAIDS_ROOM', order: 5 },
    { value: MiscellaneousDetails.SalesBuildingAllowsPets, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.BUILDING_ALLOWS_PETS', order: 6 },
    { value: MiscellaneousDetails.LoftSpace, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.LOFT_SPACE', order: 7 },
    { value: MiscellaneousDetails.Penthouse, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.PENTHOUSE', order: 8 },
    { value: MiscellaneousDetails.Fireplace, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.FIREPLACE', order: 9 }
];

const RENTALS_MISCELLANEOUS_DETAILS_VALUES: CheckboxFilterValue[] = [
    { value: MiscellaneousDetails.WasherDryer, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.WASHER_DRYER', order: 1 },
    { value: MiscellaneousDetails.SponsorApartment, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.SPONSOR_APARTMENT', order: 2 },
    { value: MiscellaneousDetails.DiningRoom, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.DINING_ROOM', order: 3 },
    { value: MiscellaneousDetails.EatInKitchen, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.EAT_IN_KITCHEN', order: 4 },
    { value: MiscellaneousDetails.MaidsRoom, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.MAIDS_ROOM', order: 5 },
    { value: MiscellaneousDetails.RentalsBuildingAllowsPets, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.BUILDING_ALLOWS_PETS', order: 6 },
    { value: MiscellaneousDetails.LoftSpace, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.LOFT_SPACE', order: 7 },
    { value: MiscellaneousDetails.Penthouse, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.PENTHOUSE', order: 8 },
    { value: MiscellaneousDetails.Fireplace, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.MISCELLANEOUS_DETAILS.FIREPLACE', order: 9 }
];

const SALES_OWNERSHIP_TYPES_VALUES: CheckboxFilterValue[] = [
    { value: OwnershipTypes.Coop, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.OWNERSHIP_TYPES.COOP', order: 1 },
    { value: OwnershipTypes.Condo, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.OWNERSHIP_TYPES.CONDO', order: 2 },
    { value: OwnershipTypes.Condop, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.OWNERSHIP_TYPES.CONDOP', order: 3 },
    { value: OwnershipTypes.Townhouse, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.OWNERSHIP_TYPES.TOWNHOUSE', order: 4 }
];

const RENTALS_OWNERSHIP_TYPES_VALUES: CheckboxFilterValue[] = [
    { value: OwnershipTypes.Coop, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.OWNERSHIP_TYPES.COOP', order: 1 },
    { value: OwnershipTypes.Condo, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.OWNERSHIP_TYPES.CONDO', order: 2 },
    { value: OwnershipTypes.Condop, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.OWNERSHIP_TYPES.CONDOP', order: 3 },
    { value: OwnershipTypes.Townhouse, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.OWNERSHIP_TYPES.TOWNHOUSE', order: 4 },
    { value: OwnershipTypes.Rental, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.OWNERSHIP_TYPES.RENTAL', order: 5 }
];

const PRIVATE_OUTDOOR_SPACES_VALUES: CheckboxFilterValue[] = [
    { value: PrivateOutdoorSpaces.Any, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.ANY', order: 1 },
    { value: PrivateOutdoorSpaces.Balcony, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.PRIVATE_OUTDOOR_SPACES.BALCONY', order: 2 },
    { value: PrivateOutdoorSpaces.Deck, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.PRIVATE_OUTDOOR_SPACES.DECK', order: 3 },
    { value: PrivateOutdoorSpaces.Garden, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.PRIVATE_OUTDOOR_SPACES.GARDEN', order: 4 },
    { value: PrivateOutdoorSpaces.Greenhouse, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.PRIVATE_OUTDOOR_SPACES.GREENHOUSE', order: 5 },
    { value: PrivateOutdoorSpaces.Patio, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.PRIVATE_OUTDOOR_SPACES.PATIO', order: 6 },
    { value: PrivateOutdoorSpaces.PrivateRoofAccess, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.PRIVATE_OUTDOOR_SPACES.PRIVATE_ROOF_ACCESS', order: 7 },
    { value: PrivateOutdoorSpaces.RoofDeck, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.PRIVATE_OUTDOOR_SPACES.ROOF_DECK', order: 8 },
    { value: PrivateOutdoorSpaces.RoofRights, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.PRIVATE_OUTDOOR_SPACES.ROOF_RIGHTS', order: 9 },
    { value: PrivateOutdoorSpaces.Terrace, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.PRIVATE_OUTDOOR_SPACES.TERRACE', order: 10 },
    { value: PrivateOutdoorSpaces.None, valueLocalizationKey: 'ON_MARKET_FILTER.FILTERS.PRIVATE_OUTDOOR_SPACES.NONE', order: 11 }
];

export const createBedsValues = (studioLocalizationKey: string): CheckboxFilterValue[] => [
    { value: Beds.Studio, valueLocalizationKey: studioLocalizationKey, order: 1 },
    { value: Beds.One, valueLocalizationKey: '1', order: 2 },
    { value: Beds.Two, valueLocalizationKey: '2', order: 3 },
    { value: Beds.Three, valueLocalizationKey: '3', order: 4 },
    { value: Beds.Four, valueLocalizationKey: '4', order: 5 }
];

export const SEARCH_FILTER_INITIAL_VALUES = {
    salesPriceMinMaxData: SALES_PRICE_MIN_MAX_DATA,
    rentalsPriceMinMaxData: RENTALS_PRICE_MIN_MAX_DATA,
    bathsValues: BATHS_VALUES,
    roomsValues: ROOMS_VALUES,
    buildingPeriodValues: BUILDING_PERIOD_VALUES,
    attendedLobbiesValues: ATTENDED_LOBBIES_VALUES,
    buildingTypesValues: BUILDING_TYPES_VALUES,
    commonOutdoorSpacesValues: COMMON_OUTDOOR_SPACES_VALUES,
    featuresValues: FEATURES_VALUES,
    salesMiscellaneousDetailsValues: SALES_MISCELLANEOUS_DETAILS_VALUES,
    rentalsMiscellaneousDetailsValues: RENTALS_MISCELLANEOUS_DETAILS_VALUES,
    salesOwnershipTypesValues: SALES_OWNERSHIP_TYPES_VALUES,
    rentalsOwnershipTypesValues: RENTALS_OWNERSHIP_TYPES_VALUES,
    privateOutdoorSpacesValues: PRIVATE_OUTDOOR_SPACES_VALUES
} as const;